import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  KeyRound, Loader2, CheckCircle2, AlertOctagon, Trash2,
  Eye, EyeOff, RefreshCw, TrendingUp, Radar,
  ChevronRight, ChevronDown, ExternalLink, Calendar, Zap, CreditCard,
} from "lucide-react";

/**
 * AHREFS Master — single-page master-settings tab for the agency-wide
 * AHREFS API key. Mirrors the SendGrid Master layout: key entry ↑
 * status card ↕ usage-snapshot cards ↓.
 *
 * The /ahrefs/master GET is what the UI uses to decide whether to show
 * the "Configured" pill or the paste-key form; the /verify POST forces
 * a fresh probe of AHREFS' /subscription/limits-and-usage (which is
 * free and non-consuming — safe to click freely).
 */
export default function AhrefsMasterPage() {
  const [state, setState] = useState(null);   // {configured, last_verified_at, usage_snapshot}
  const [loading, setLoading] = useState(true);
  const [keyDraft, setKeyDraft] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(null);   // "save" | "verify" | "clear" | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ahrefs/master");
      setState(data);
      // If configured, silently refresh the usage snapshot in the
      // background so the dashboard always reflects the CURRENT
      // subscription cycle. The endpoint is free — no units are
      // consumed by /subscription-info/limits-and-usage — so we can
      // run it on every page load without cost concerns.
      if (data?.configured) {
        try {
          const v = await api.post("/ahrefs/master/verify");
          if (v.data?.ok) {
            setState((s) => ({ ...(s || {}), configured: true,
              last_verified_at: new Date().toISOString(),
              usage_snapshot: v.data.usage }));
          }
        } catch { /* silent — user can click Test connection manually */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load AHREFS state");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    if (!keyDraft.trim()) { toast.error("Paste an API key first"); return; }
    setBusy("save");
    try {
      const { data } = await api.put("/ahrefs/master/api-key",
        { api_key: keyDraft.trim() });
      toast.success("AHREFS key verified & saved");
      setKeyDraft(""); setShowKey(false);
      setState((s) => ({ ...(s || {}), configured: true,
        last_verified_at: new Date().toISOString(),
        usage_snapshot: data.usage }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the AHREFS master API key? All AHREFS data-fetching will stop until a new key is entered.")) return;
    setBusy("clear");
    try {
      await api.delete("/ahrefs/master/api-key");
      toast.success("Cleared");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(null); }
  };

  const verify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.post("/ahrefs/master/verify");
      if (data.ok) {
        toast.success("AHREFS connection healthy");
        setState((s) => ({ ...(s || {}),
          last_verified_at: new Date().toISOString(),
          usage_snapshot: data.usage }));
      } else {
        toast.error(`AHREFS rejected: ${data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setBusy(null); }
  };

  const usage = state?.usage_snapshot || null;
  const configured = state?.configured;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="ahrefs-master-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#F97316]/10 grid place-items-center">
          <Radar className="h-5 w-5 text-[#F97316]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            AHREFS Master
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Agency-wide AHREFS API key + workspace usage. Every client&apos;s
            per-property AHREFS assignments authenticate through this
            master key.
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" />
            <div className="label-overline">API key</div>
            {loading ? (
              <Badge variant="outline"
                className="ml-2 rounded-sm text-[10px] font-mono">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> loading
              </Badge>
            ) : configured ? (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-emerald-100 text-emerald-800 border-emerald-200"
                data-testid="ahrefs-master-status-configured">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Configured
              </Badge>
            ) : (
              <Badge className="ml-2 rounded-sm text-[10px] font-mono
                bg-amber-100 text-amber-800 border-amber-200"
                data-testid="ahrefs-master-status-missing">
                <AlertOctagon className="h-3 w-3 mr-1" /> Not configured
              </Badge>
            )}
          </div>

          <div>
            <Label className="label-overline">
              {configured ? "Replace / rotate key" : "Paste your AHREFS API key"}
            </Label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)}
                  placeholder="Paste key from AHREFS → Account settings → API keys"
                  className="rounded-sm font-mono text-[12px] pr-8"
                  data-testid="ahrefs-master-key-input" />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <Button onClick={saveKey} disabled={busy !== null || !keyDraft.trim()}
                className="rounded-sm bg-[#F97316] hover:bg-[#EA580C] text-white"
                data-testid="ahrefs-master-save-btn">
                {busy === "save"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : "Save & verify"}
              </Button>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-1.5 leading-relaxed">
              Retrieve it from{" "}
              <a href="https://app.ahrefs.com/user/api" target="_blank" rel="noreferrer"
                className="text-[#F97316] hover:underline">
                app.ahrefs.com/user/api
              </a>{" "}
              — workspace owner or admin required. Key is encrypted at rest;
              never leaves the server after decryption.
            </p>
          </div>

          <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
            <Button size="sm" variant="outline" onClick={verify}
              disabled={busy !== null || !configured}
              className="rounded-sm"
              data-testid="ahrefs-master-verify-btn">
              {busy === "verify"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Test connection
            </Button>
            <Button size="sm" variant="outline" onClick={clearKey}
              disabled={busy !== null || !configured}
              className="rounded-sm text-red-600 hover:bg-red-50"
              data-testid="ahrefs-master-clear-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear key
            </Button>
            {state?.last_verified_at && (
              <span className="ml-auto text-[10px] font-mono text-zinc-500">
                Last verified {new Date(state.last_verified_at).toLocaleString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {usage && (
        <AhrefsUsageDashboard usage={usage} />
      )}

      {/* Clients using AHREFS — aggregated summary from cached snapshots */}
      {state?.configured && (
        <ClientsSummarySection />
      )}
    </div>
  );
}


/**
 * ClientsSummarySection — table of every client with AHREFS assignments.
 * Aggregated headline metrics come from the last cached snapshot on each
 * assignment (see the per-property Refresh flow on the Feeds page).
 * Clients that have never been refreshed show a "no data yet" state.
 */
function ClientsSummarySection() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ahrefs/clients-summary");
      setRows(data.clients || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load clients summary");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (cid) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(cid)) n.delete(cid); else n.add(cid);
    return n;
  });

  const fmt = (v, digits = 0, prefix = "") => {
    if (v == null) return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return "—";
    return prefix + n.toLocaleString(undefined,
      { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ahrefs-clients-summary">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div>
            <div className="label-overline flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> Clients using AHREFS
            </div>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Every client with at least one AHREFS assignment. Metrics are
              the sum across all assigned properties from the most recent
              cached snapshot. Refresh a property from its Feeds page to
              update these figures.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="rounded-sm" data-testid="ahrefs-clients-summary-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                     : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Reload
          </Button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-[12px] text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : (rows || []).length === 0 ? (
          <div className="p-6 text-center text-[12px] text-zinc-500 border
            border-dashed border-zinc-300 rounded-sm">
            No clients have AHREFS assignments yet.
            Head to any client&apos;s <strong>AHREFS Feeds</strong> tab to add one.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((c) => {
              const t = c.totals || {};
              const stale = c.properties_with_data < c.assignment_count;
              const isOpen = expanded.has(c.client_id);
              const props = c.properties || [];
              // Best guess at the "primary" property URL for the
              // dashboard-header link — the first assigned property.
              const primaryTarget = (props[0] || {}).target;
              // DR colour band (matches AHREFS' Health-Score dot):
              const dr = t.domain_rating_max || 0;
              const drColor =
                dr >= 60 ? "#10b981" :
                dr >= 40 ? "#3b82f6" :
                dr >= 20 ? "#f59e0b" : "#94a3b8";

              return (
                <div key={c.client_id}
                  className="rounded-sm border border-zinc-200 bg-white overflow-hidden"
                  data-testid={`ahrefs-client-row-${c.client_id}`}>
                  {/* ── Header: brand + name + primary domain + manage ── */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3
                    bg-zinc-50 border-b border-zinc-200 cursor-pointer
                    hover:bg-zinc-100 transition-colors"
                    onClick={() => toggle(c.client_id)}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      {c.brand_primary ? (
                        <span className="inline-block h-8 w-8 rounded-sm
                          border border-zinc-300 shrink-0"
                          style={{ backgroundColor: c.brand_primary }} />
                      ) : (
                        <span className="inline-block h-8 w-8 rounded-sm
                          bg-zinc-200 border border-zinc-300 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-display font-bold text-[15px] truncate">
                            {c.company_name}
                          </span>
                          {stale && (
                            <Badge variant="outline"
                              className="rounded-sm text-[8px] font-mono
                                bg-amber-50 border-amber-300 text-amber-800">
                              {c.assignment_count - c.properties_with_data} stale
                            </Badge>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500 truncate">
                          {primaryTarget || `${c.assignment_count} properties`}
                          {c.last_snapshot_at && (
                            <span className="ml-2 text-zinc-400">
                              · last refresh {new Date(c.last_snapshot_at).toLocaleDateString(
                                undefined, { day: "numeric", month: "short" })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`/clients/${c.client_id}/ahrefs-feeds`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[11px] text-[#F97316] hover:underline
                          font-semibold whitespace-nowrap"
                        data-testid={`ahrefs-client-goto-${c.client_id}`}>
                        Manage →
                      </a>
                      {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" />
                              : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                    </div>
                  </div>

                  {/* ── Big metric tiles row (AHREFS-style dashboard) ── */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6
                    gap-0 border-b border-zinc-200">
                    <MetricTile label="Health Score"
                      value={dr ? Math.min(100, Math.round(dr * 1.5 + 10)) : null}
                      variant="score" scoreColor={drColor} />
                    <MetricTile label="Domain Rating"
                      value={t.domain_rating_max} digits={1} />
                    <MetricTile label="Referring domains"
                      value={t.refdomains} />
                    <MetricTile label="Organic traffic"
                      value={t.org_traffic}
                      subtitle={t.org_traffic_value
                        ? `Value: $${Math.round(t.org_traffic_value).toLocaleString()}`
                        : null} />
                    <MetricTile label="Organic keywords"
                      value={t.org_keywords} />
                    <MetricTile label="Properties"
                      value={c.assignment_count}
                      subtitle={`${c.properties_with_data} refreshed`} />
                  </div>

                  {/* ── Expandable properties list ── */}
                  {isOpen && (
                    <div className="divide-y divide-zinc-100">
                      {props.map((p) => {
                        const pt = p.totals || {};
                        return (
                          <div key={p.assignment_id}
                            className="grid grid-cols-12 gap-2 px-4 py-2
                              text-[11px] hover:bg-zinc-50 items-center"
                            data-testid={`ahrefs-property-row-${p.assignment_id}`}>
                            <div className="col-span-4 min-w-0 flex items-center gap-2">
                              <span className="text-zinc-300 font-mono">└</span>
                              <div className="min-w-0">
                                <div className="font-mono text-[11px] text-zinc-700 truncate">
                                  {p.target}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <Badge className={`rounded-sm text-[8px] font-mono ${
                                    p.source === "project"
                                      ? "bg-emerald-100 text-emerald-800"
                                      : "bg-amber-100 text-amber-800"}`}>
                                    {p.source === "project" ? "PROJECT" : "CUSTOM"}
                                  </Badge>
                                  <span className="text-[9px] text-zinc-500 truncate">
                                    {p.business_unit_name || "(unassigned BU)"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <PropMetric label="DR" value={pt.domain_rating} digits={1} />
                            <PropMetric label="Ref. domains"
                              value={pt.referring_domains_sample ?? pt.refdomains}
                              muted={!p.has_data} />
                            <PropMetric label="Org. traffic"
                              value={pt.org_traffic}
                              muted={!p.has_data} />
                            <PropMetric label="Org. keywords"
                              value={pt.org_keywords}
                              muted={!p.has_data} />
                            <PropMetric label="Backlinks"
                              value={pt.backlinks_sample ?? pt.backlinks}
                              muted={!p.has_data} />
                            <div className="col-span-1 text-right">
                              <a href={`/clients/${c.client_id}/ahrefs-feeds`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-[10px] text-zinc-500 hover:text-[#F97316]
                                  whitespace-nowrap font-semibold"
                                data-testid={`ahrefs-property-goto-${p.assignment_id}`}>
                                Open →
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * MetricTile — one of the six big-number cards at the top of each
 * client row. Mirrors AHREFS' dashboard tile aesthetic: tiny grey
 * label at top, large bold number, optional subtitle below. When
 * `variant="score"` we render the value inside a coloured circle
 * (Health-Score style) to match AHREFS' green badge.
 */
function MetricTile({ label, value, subtitle, digits = 0, variant,
                     scoreColor = "#10b981" }) {
  const display = value == null ? "—"
    : Number(value).toLocaleString(undefined,
        { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return (
    <div className="p-3 border-r last:border-r-0 border-zinc-100
      last:border-r-0 md:last:border-r border-b md:border-b-0">
      <div className="text-[9px] font-mono uppercase tracking-wider
        text-zinc-500 mb-1.5">
        {label}
      </div>
      {variant === "score" ? (
        <div className="flex items-center justify-center h-8 w-8
          rounded-full text-white font-display font-bold text-[13px]"
          style={{ backgroundColor: value ? scoreColor : "#cbd5e1" }}>
          {value ?? "—"}
        </div>
      ) : (
        <div className="font-display font-bold text-[20px] leading-tight">
          {display}
        </div>
      )}
      {subtitle && (
        <div className="text-[9px] font-mono text-zinc-500 mt-1">
          {subtitle}
        </div>
      )}
    </div>
  );
}

/**
 * PropMetric — small labelled value cell used in the expandable
 * property sub-rows below each client card. Two-line stack: mono
 * label on top, bold value below. `muted` greys the whole cell when
 * the property has no cached snapshot.
 */
function PropMetric({ label, value, digits = 0, muted }) {
  const display = value == null ? "—"
    : Number(value).toLocaleString(undefined,
        { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return (
    <div className={`col-span-1 md:col-span-1 lg:col-span-1
      ${muted ? "text-zinc-400" : ""}`}>
      <div className="text-[8px] font-mono uppercase tracking-wider text-zinc-400">
        {label}
      </div>
      <div className="font-mono font-semibold text-[12px]">
        {display}
      </div>
    </div>
  );
}


/**
 * AhrefsUsageDashboard — rich, dashboard-style rendering of the
 * `/subscription-info/limits-and-usage` payload from AHREFS. Replaces
 * the old raw key/value grid with:
 *
 *   1. Subscription card — plan name + billing cycle + days until
 *      the usage counter resets.
 *   2. Workspace units meter — huge progress bar with used / limit /
 *      remaining and a "burn-rate" projection warning if usage is
 *      trending to exhaust before the reset date.
 *   3. API-key card — usage on the specific API key we're using plus
 *      the key's own expiration date.
 *   4. Reference footer — link to AHREFS pricing docs so admins can
 *      upgrade the plan directly if the projection warns exhaustion.
 */
function AhrefsUsageDashboard({ usage }) {
  const plan = usage.subscription || "—";
  const resetIso = usage.usage_reset_date;
  const keyExpIso = usage.api_key_expiration_date;
  const wsUsed = Number(usage.units_usage_workspace ?? 0);
  const wsLimit = Number(usage.units_limit_workspace ?? 0);
  const keyUsed = Number(usage.units_usage_api_key ?? 0);
  const keyLimit = usage.units_limit_api_key;   // may be null = unlimited

  const wsPct = wsLimit > 0 ? Math.min(100, (wsUsed / wsLimit) * 100) : 0;
  const wsRemaining = Math.max(0, wsLimit - wsUsed);
  const wsColor = wsPct >= 90
    ? "bg-red-500"
    : wsPct >= 75
      ? "bg-amber-500"
      : "bg-emerald-500";

  // Days-until-reset + burn-rate projection.
  const now = new Date();
  let daysToReset = null;
  let cycleDaysTotal = null;
  let cycleDaysElapsed = null;
  let projectedEnd = null;
  if (resetIso) {
    const reset = new Date(resetIso);
    daysToReset = Math.max(0, Math.ceil((reset - now) / (1000 * 60 * 60 * 24)));
    // Assume monthly billing → 30-day cycle. Elapsed = 30 - daysToReset.
    cycleDaysTotal = 30;
    cycleDaysElapsed = Math.max(1, cycleDaysTotal - daysToReset);
    const dailyBurn = wsUsed / cycleDaysElapsed;
    projectedEnd = Math.round(dailyBurn * cycleDaysTotal);
  }
  const projectedPct = wsLimit > 0 && projectedEnd
    ? (projectedEnd / wsLimit) * 100
    : 0;
  const burnWarn = projectedPct > 100;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(undefined,
        { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="ahrefs-usage-snapshot">
      {/* Row 1 — plan + reset + key expiration cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="rounded-sm border-zinc-200"
          data-testid="ahrefs-plan-card">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#F97316]/10 grid place-items-center shrink-0">
              <CreditCard className="h-4 w-4 text-[#F97316]" />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                Subscription
              </div>
              <div className="font-display font-bold text-lg text-zinc-900 leading-tight">
                {plan}
              </div>
              <a href="https://ahrefs.com/api/pricing" target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-sky-700 hover:underline
                  inline-flex items-center gap-1 mt-1">
                Plan details & pricing <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-zinc-200"
          data-testid="ahrefs-reset-card">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-sm bg-emerald-500/10 grid place-items-center shrink-0">
              <Calendar className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                Units reset date
              </div>
              <div className="font-display font-bold text-base text-zinc-900 leading-tight">
                {fmtDate(resetIso)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {daysToReset == null ? "—" : `${daysToReset} day${daysToReset === 1 ? "" : "s"} from today`}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-zinc-200"
          data-testid="ahrefs-keyexp-card">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-sm bg-sky-500/10 grid place-items-center shrink-0">
              <KeyRound className="h-4 w-4 text-sky-600" />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                API key expires
              </div>
              <div className="font-display font-bold text-base text-zinc-900 leading-tight">
                {fmtDate(keyExpIso)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                Regenerate before this date to avoid data outages
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2 — workspace usage meter */}
      <Card className="rounded-sm border-zinc-200"
        data-testid="ahrefs-workspace-meter">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#F97316]" />
              <div className="label-overline">Workspace units this cycle</div>
            </div>
            <div className="text-[11px] font-mono text-zinc-600">
              <span className="text-[#F97316] font-bold text-base">
                {wsUsed.toLocaleString()}
              </span>
              <span className="text-zinc-400 mx-1">/</span>
              <span>{wsLimit.toLocaleString()}</span>
              <span className="text-zinc-500 ml-2">
                · {wsPct.toFixed(1)}% used · {wsRemaining.toLocaleString()} remaining
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-4 rounded-sm bg-zinc-100 overflow-hidden
            relative border border-zinc-200">
            <div className={`h-full ${wsColor} transition-all duration-500`}
              style={{ width: `${wsPct}%` }}
              data-testid="ahrefs-workspace-fill" />
            {/* Projected-end marker — a vertical tick showing where
                the counter is expected to land at the end of the cycle
                if usage continues at today's rate. Red when past limit. */}
            {projectedEnd != null && projectedPct > 0 && (
              <div className={`absolute top-0 h-full w-0.5
                ${burnWarn ? "bg-red-700" : "bg-zinc-700"}`}
                style={{ left: `${Math.min(100, projectedPct)}%` }}
                title={`Projected end-of-cycle: ${projectedEnd.toLocaleString()} units (${projectedPct.toFixed(1)}%)`} />
            )}
          </div>

          {/* Burn-rate projection callout */}
          {projectedEnd != null && cycleDaysElapsed > 0 && (
            <div className={`mt-3 rounded-sm px-3 py-2 text-[11px]
              ${burnWarn
                ? "bg-red-50 border border-red-200 text-red-800"
                : "bg-zinc-50 border border-zinc-200 text-zinc-700"}`}
              data-testid="ahrefs-burn-rate">
              <strong>Burn-rate:</strong>{" "}
              {Math.round(wsUsed / cycleDaysElapsed).toLocaleString()} units/day
              · projected end-of-cycle total: <strong>{projectedEnd.toLocaleString()}</strong>
              {" "}({projectedPct.toFixed(0)}%)
              {burnWarn && (
                <> — <strong>Warning:</strong> at this pace you&apos;ll
                exhaust your quota <strong>~
                {Math.max(1, Math.ceil((wsLimit - wsUsed)
                  / Math.max(1, wsUsed / cycleDaysElapsed)))}
                </strong> days before the reset.
                <a href="https://ahrefs.com/api/pricing" target="_blank" rel="noopener noreferrer"
                  className="underline ml-1 inline-flex items-center gap-0.5">
                  Upgrade plan <ExternalLink className="h-2.5 w-2.5" />
                </a></>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 3 — API-key usage (only rendered if AHREFS returned key-scope data) */}
      {keyUsed > 0 && (
        <Card className="rounded-sm border-zinc-200"
          data-testid="ahrefs-key-usage">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-sky-600" />
                <div className="label-overline">This API key&apos;s usage</div>
              </div>
              <div className="text-[11px] font-mono text-zinc-600">
                <span className="text-sky-700 font-bold text-base">
                  {keyUsed.toLocaleString()}
                </span>
                <span className="text-zinc-400 mx-1">/</span>
                <span>{keyLimit == null ? "∞ workspace pool"
                  : Number(keyLimit).toLocaleString()}</span>
                <span className="text-zinc-500 ml-2">
                  · {((keyUsed / (wsUsed || 1)) * 100).toFixed(1)}% of workspace total
                </span>
              </div>
            </div>
            {keyLimit != null && (
              <div className="w-full h-3 rounded-sm bg-zinc-100 overflow-hidden
                border border-zinc-200">
                <div className="h-full bg-sky-500 transition-all duration-500"
                  style={{ width: `${Math.min(100, (keyUsed / Number(keyLimit)) * 100)}%` }} />
              </div>
            )}
            <p className="text-[10px] text-zinc-500 mt-2">
              {keyLimit == null
                ? "This key draws from the shared workspace pool with no per-key sub-limit."
                : "This key has its own per-key allowance beneath the workspace limit."}
            </p>
          </CardContent>
        </Card>
      )}

      <p className="text-[10px] font-mono text-zinc-400">
        Stats auto-refresh every time you open this page &mdash; the
        AHREFS <code>/subscription-info/limits-and-usage</code> endpoint
        is free and does not consume any units.
      </p>
    </div>
  );
}

